import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { Toaster } from "../components/Toaster";

export type ToastEntry = {
  id: string;
  message: string;
  /** Optional action button (e.g. undo). */
  action?: { label: string; onClick: () => void };
  /** Milliseconds before auto-dismiss. Pass Infinity to require manual
   *  dismissal. Defaults to 5000. */
  duration?: number;
  /** Visual treatment. Defaults to neutral / lavender accent. */
  kind?: "default" | "success" | "danger";
};

type ToastFn = (entry: Omit<ToastEntry, "id">) => string;

const ToastContext = createContext<{ toast: ToastFn; dismiss: (id: string) => void } | null>(
  null
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastFn>((entry) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...entry, id }]);
    return id;
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}
